import React from "react";

/**
 * Formatea un valor monetario a pesos mexicanos.
 */
const formatearPrecio = (valor) => {
    if (valor == null) return "—";
    return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(valor);
};

/**
 * Resumen ejecutivo de la cotización: responde de un vistazo qué cotización es,
 * de qué cliente/ruta, si es nueva o modificación, cuántos km/estructuras tiene
 * y cuál es el precio al cliente. Si es una modificación, además muestra qué cambió
 * respecto a la versión anterior.
 */
const ResumenEjecutivo = ({ cotizacion, visibilidad }) => {
    const ruta = cotizacion.rutas;
    const rutaAnterior = cotizacion.rutaAnterior;
    const cambiosRuta = cotizacion.cambiosRuta ?? [];
    const historial = cotizacion.historial ?? [];

    const esNueva = cotizacion.idCategoria === 3;
    const esModificacion = cotizacion.idCategoria === 5;

    // Mismo criterio que en TarjetaRuta: en modificación, los datos "de cabecera"
    // (km, tiempo, estructuras) se toman de la ruta anterior como referencia.
    const datosHeader = esNueva ? ruta : rutaAnterior;

    const cambioKm = cambiosRuta.find((c) => c.campo?.toLowerCase().includes("kilometraje"));
    const precioAnterior = rutaAnterior?.precioCliente;
    const precioActual = ruta?.precioCliente;
    const cambioPrecio = precioAnterior != null && precioActual != null && precioAnterior !== precioActual;

    return (
        <div className="resumen-ejecutivo">
            <div className="resumen-ejecutivo__identidad">
                <div className="resumen-ejecutivo__dato">
                    <span className="resumen-ejecutivo__dato-label">Cliente</span>
                    <span className="resumen-ejecutivo__dato-valor">
                        {cotizacion.clienteNombre || `Cliente #${cotizacion.cliente}`}
                    </span>
                </div>
                <div className="resumen-ejecutivo__dato">
                    <span className="resumen-ejecutivo__dato-label">Ruta</span>
                    <span className="resumen-ejecutivo__dato-valor">{ruta?.nombre || "—"}</span>
                </div>
                <div className="resumen-ejecutivo__dato">
                    <span className="resumen-ejecutivo__dato-label">Tipo</span>
                    <span className="resumen-ejecutivo__dato-valor">
                        {esModificacion ? "Modificación" : "Nueva ruta"}
                    </span>
                </div>
                <div className="resumen-ejecutivo__dato">
                    <span className="resumen-ejecutivo__dato-label">Responsable</span>
                    <span className="resumen-ejecutivo__dato-valor">
                        {cotizacion.usuarioNombre || `Usuario #${cotizacion.usuario}`}
                    </span>
                </div>
            </div>

            <div className="resumen-ejecutivo__cuerpo">
                <div className="resumen-ejecutivo__metricas">
                    {visibilidad.kilometraje && datosHeader?.kilometros > 0 && (
                        <span className="resumen-ejecutivo__metrica">
                            <i className="pi pi-map-marker" />
                            {datosHeader.kilometros} km
                        </span>
                    )}
                    {visibilidad.kilometraje && datosHeader?.tiempoEstimado != null && (
                        <span className="resumen-ejecutivo__metrica">
                            <i className="pi pi-clock" />
                            {datosHeader.tiempoEstimado} min
                        </span>
                    )}
                    {visibilidad.kilometraje && datosHeader?.cantidadEstructuras != null && (
                        <span className="resumen-ejecutivo__metrica">
                            <i className="pi pi-th-large" />
                            {datosHeader.cantidadEstructuras} estructuras
                        </span>
                    )}
                </div>

                {visibilidad.precioCliente && precioActual != null && (
                    <div className="resumen-ejecutivo__precio">
                        <span className="resumen-ejecutivo__precio-label">Precio Cliente</span>
                        <span className="resumen-ejecutivo__precio-valor">{formatearPrecio(precioActual)}</span>
                    </div>
                )}
            </div>

            {esModificacion && (cambioKm || cambioPrecio || historial.length > 0) && (
                <div className="resumen-ejecutivo__modificacion">
                    <span className="resumen-ejecutivo__modificacion-flag">
                        <i className="pi pi-sync" /> MODIFICACIÓN
                    </span>

                    <div className="resumen-ejecutivo__modificacion-detalle">
                        {historial.map((h) => (
                            <span key={h.id} className="resumen-ejecutivo__check">
                                <i className="pi pi-check" /> {h.nombreCambio}
                            </span>
                        ))}
                        {cambioKm && (
                            <span className="resumen-ejecutivo__check">
                                <i className="pi pi-check" /> Kilometraje: {cambioKm.anterior} → {cambioKm.actual} km
                            </span>
                        )}
                        {cambioPrecio && (
                            <span className="resumen-ejecutivo__check">
                                <i className="pi pi-check" /> Precio cliente: {formatearPrecio(precioAnterior)} → {formatearPrecio(precioActual)}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {esNueva && (
                <div className="resumen-ejecutivo__nueva-flag">
                    <i className="pi pi-verified" /> NUEVA RUTA
                </div>
            )}
        </div>
    );
};

export default ResumenEjecutivo;
